import axios from "axios";
import { AudienceProvisionFetchFailedError, AudienceProvisionSaveFailedError, MadSDKAbortError, NotImplementedError } from "../../errors";
import { serviceOrgListItemToProvision, serviceProvisionToProvision, provisionToServiceProvision, ValidOrgTypesForProvisioning } from "../../models/audienceProvision";
import { parseNewNewYorkApiResponse } from "../../utils";
import { FilterTypes, parseIdFilter } from "../../handlers/filter";
import { PromiseHandler } from "../../handlers";
/**
 * Class for handling audience provisioning CRUD operations.
 */
class AudienceProvisionsHandler extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "audienceProvisions");
    }
    /**
     * Handles finding specific audienceProvision based on filters given. If no filters are
     * given, a list of all audience provisions is returned.
     *
     * @param filters filters to use within the find, currently only "id", "eq" is supported
     */
    async findItems(filters) {
        const idFilter = parseIdFilter(filters);
        const promise = idFilter.size === 1
            ? this.getOrgProvisionDetails(idFilter.values().next().value).then((provision) => [
                provision
            ])
            : this.getOrgProvisionList();
        return promise.catch((error) => {
            throw new AudienceProvisionFetchFailedError(error);
        });
    }
    /**
     * Saves audience provision data (packages/tiers) for an organization.
     *
     * @param data AudienceProvision to be updated.
     */
    async saveItem(data) {
        const url = `${this.sdk.urls.baseAPIUrl}/organization/${data.orgId}/audienceProvision`;
        const cancellationSource = axios.CancelToken.source();
        // Store the original audience links to preserve them
        const originalAudienceLinks = data.audienceLinks || {};
        // Clear own cache
        this.cache.clear();
        // Clear related caches - invalidate the orgDmps cache for this organization
        if (data.orgId && this.sdk.orgDmps) {
            this.sdk.orgDmps.invalidateOrgDmpsCache(data.orgId);
        }
        return new Promise((resolve, reject) => {
            axios
                .post(url, this.toServiceAudienceProvision(data), {
                headers: {
                    "Content-Type": "application/json"
                },
                cancelToken: cancellationSource.token
            })
                .then((res) => {
                try {
                    // Transform the response data to client format
                    const updatedProvision = this.toAudienceProvision(res.data?.data);
                    // Re-attach the original audience links if they exist
                    if (Object.keys(originalAudienceLinks).length > 0) {
                        updatedProvision.audienceLinks = originalAudienceLinks;
                    }
                    else {
                        // If no original links, ensure we at least have an empty object
                        updatedProvision.audienceLinks = updatedProvision.audienceLinks || {};
                    }
                    resolve(updatedProvision);
                }
                catch (error) {
                    reject(new AudienceProvisionSaveFailedError(error));
                }
            })
                .catch((error) => {
                if (axios.isCancel(error)) {
                    reject(new MadSDKAbortError());
                }
                else {
                    reject(error);
                }
            });
        });
    }
    /**
     * NOT IMPLEMENTED - base class requires an implementation.
     */
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * Fetches audience data provisioning details for a particular organization.
     * @param id: an audienceProvision id.
     * @return: the audienceProvision with that id.
     */
    getOrgProvisionDetails(id) {
        const url = `${this.sdk.urls.baseAPIUrl}/organization/${id}/audienceProvision`;
        return this.cache.promise(id, async () => {
            try {
                // First, fetch the audience provision data from the API
                const res = await axios.get(url, {
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
                const { data: rawAudienceProvision } = parseNewNewYorkApiResponse(res);
                // Transform the data to client format
                const audienceProvision = this.toAudienceProvision(rawAudienceProvision);
                // Now fetch audience links separately using the AudienceLinksHandler
                try {
                    // Use the AudienceLinksHandler to fetch audience links for this organization
                    const audienceLinks = await this.sdk.audienceLinks.find({
                        where: [{ field: "id", type: FilterTypes.EQ, value: id }]
                    });
                    // Create a record of audience links indexed by DMP ID
                    if (audienceLinks && audienceLinks.length > 0) {
                        const linksRecord = {};
                        // Add each audience link to the record, using the DMP ID as the key
                        audienceLinks.forEach((link) => {
                            if (link.dmpId) {
                                linksRecord[link.dmpId] = link;
                            }
                        });
                        // Attach the audience links to the audience provision
                        audienceProvision.audienceLinks = linksRecord;
                    }
                    else {
                        // Ensure audienceLinks is at least an empty object
                        audienceProvision.audienceLinks = {};
                    }
                }
                catch (linkError) {
                    // Ensure audienceLinks is at least an empty object if the fetch fails
                    audienceProvision.audienceLinks = audienceProvision.audienceLinks || {};
                }
                return audienceProvision;
            }
            catch (error) {
                throw new AudienceProvisionFetchFailedError(error);
            }
        });
    }
    /**
     * Fetches a list of all orgs and necessary audience provision data.
     * @return: the list of all org audience provisions.
     */
    getOrgProvisionList() {
        const url = `${this.sdk.urls.baseAPIUrl}/organizations?orgTypes=${ValidOrgTypesForProvisioning.join("%2C")}`;
        return this.cache.promise(url, async () => {
            try {
                // Fetch the list of organizations first
                const res = await axios.get(url, {
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
                const { data: rawAudienceProvisions } = parseNewNewYorkApiResponse(res);
                // Transform each organization to an AudienceProvision
                const audienceProvisions = rawAudienceProvisions.map((serviceOrgListItem) => this.toAudienceProvisionFromOrgList(serviceOrgListItem));
                // For a list, we don't automatically fetch audience links for all orgs
                // as that would be too many requests. The audience links will be
                // fetched when a specific organization is selected.
                // Just ensure all have an empty audienceLinks object.
                audienceProvisions.forEach((provision) => {
                    provision.audienceLinks = provision.audienceLinks || {};
                });
                return audienceProvisions;
            }
            catch (error) {
                throw new AudienceProvisionFetchFailedError(error);
            }
        });
    }
    /**
     * Extracts necessary data from an organization and transforms it into an AudienceProvision object.
     * @param serviceOrgListItem: an organization from the BE org list service.
     * @return: the transformed AudienceProvision formatted for FE components.
     */
    toAudienceProvisionFromOrgList(serviceOrgListItem) {
        return serviceOrgListItemToProvision(serviceOrgListItem);
    }
    /**
     * Converts a ServiceAudienceProvision (BE) -> AudienceProvision (FE).
     * @param serviceAudienceProvision: an audienceProvision from the BE service.
     * @return: the transformed audienceProvision formatted for FE components.
     */
    toAudienceProvision(serviceAudienceProvision) {
        return serviceProvisionToProvision(serviceAudienceProvision);
    }
    /**
     * Converts a AudienceProvision (FE) -> ServiceAudienceProvision (BE).
     * @param audienceProvision: an audienceProvision from a FE component.
     * @return: the transformed serviceAudienceProvision formatted for the BE service.
     */
    toServiceAudienceProvision(audienceProvision) {
        return provisionToServiceProvision(audienceProvision);
    }
}
export default AudienceProvisionsHandler;
//# sourceMappingURL=index.js.map